// biome-ignore-all lint/correctness/useExhaustiveDependencies: legacy hook-dependency
// backlog. Deps here were NOT audited in the 2026-08-26 biome sweep - completing them
// changes when effects re-run, which needs per-effect judgement and on-device checking.
// Drop this block and fix the effects in this file the next time you work on it.
import { useQueries } from '@tanstack/react-query';
import { createRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { getBozpToken } from '@/api/bozp-token';
import { getTraining } from '@/api/training';
import { Masthead } from '@/components/Masthead';
import { ReturnButton } from '@/components/ReturnButton';
import { useT } from '@/i18n';
import { reportClientError, reportLandingLoaded } from '@/lib/client-report';
import type { TrainingStatus } from '@/lib/handoff';
import { decodeJwtSub } from '@/lib/jwt';
import { loadProgress } from '@/lib/progress-db';
import { getSession } from '@/session';
import { Route as rootRoute } from './__root';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hub',
  component: HubPage,
});

const STATUS_LABEL: Record<TrainingStatus, string> = {
  valid: 'Valid',
  needed: 'Needed',
  expired: 'Expired',
};
const STATUS_CHIP: Record<TrainingStatus, string> = {
  valid: 'hb-chip--valid',
  needed: 'hb-chip--needed',
  expired: 'hb-chip--expired',
};

type Progress = { done: number; total: number };

export function HubView({
  onOpenTraining,
  titleById = {},
  progressById = {},
}: {
  onOpenTraining: (hazardId: string) => void;
  titleById?: Record<string, string>;
  progressById?: Record<string, Progress>;
}) {
  const t = useT();
  const { trainings, returnUrl, embed, parentOrigin } = getSession();
  const allDone = trainings.length > 0 && trainings.every((tr) => tr.status === 'valid');

  return (
    <div className="flex flex-1 flex-col">
      <Masthead />
      <main className="mx-auto w-full max-w-3xl px-6 pb-16">
        <div className="hb-rise mb-9">
          <p className="hb-eyebrow hb-eyebrow--accent mb-2">{t('Required trainings')}</p>
          <h1 className="hb-title text-4xl sm:text-5xl">{t('Your training')}</h1>
          <p className="mt-3 max-w-xl text-ink-soft">
            {allDone ? t('Everything is complete.') : t('Complete the required trainings below.')}
          </p>
        </div>

        <ol className="flex flex-col gap-4">
          {trainings.map((tr, i) => {
            const id = tr.hazardProfileId;
            const title = titleById[id];
            const progress = progressById[id];
            const pct =
              tr.status === 'valid'
                ? 100
                : progress && progress.total > 0
                  ? Math.round((progress.done / progress.total) * 100)
                  : 0;
            const showProgress = tr.status !== 'valid' && progress && progress.total > 0;
            return (
              <li
                key={id}
                data-testid={`training-row-${id}`}
                className="hb-rise"
                style={{ animationDelay: `${i * 70 + 80}ms` }}
              >
                <article className="relative flex gap-4 rounded-[var(--hb-radius)] border border-line bg-card p-5 shadow-[0_1px_0_rgba(8,9,10,0.02)] sm:gap-6 sm:p-6">
                  <span className="hb-numeral select-none text-[2.5rem] sm:text-[3rem]">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
                      <h2 className="hb-title min-w-0 text-xl sm:text-2xl">
                        {title ?? (
                          <span className="inline-block h-5 w-44 max-w-full animate-pulse rounded bg-paper-3 align-middle" />
                        )}
                      </h2>
                      <span className={`hb-chip ${STATUS_CHIP[tr.status]}`}>
                        {t(STATUS_LABEL[tr.status])}
                      </span>
                    </div>

                    {showProgress && (
                      <div className="mt-4">
                        <div className="mb-1.5 flex items-center justify-between text-xs text-ink-faint">
                          <span>{t('Progress')}</span>
                          <span className="tabular-nums">{pct}%</span>
                        </div>
                        <div className="hb-progress" aria-hidden>
                          <span style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    )}

                    {tr.status !== 'valid' && (
                      <div className="mt-5">
                        <button
                          type="button"
                          data-testid={`open-${id}`}
                          onClick={() => onOpenTraining(id)}
                          className="hb-btn hb-btn--primary w-full sm:w-auto"
                        >
                          {pct > 0 ? t('Continue') : t('Start test')}
                          <span aria-hidden>→</span>
                        </button>
                      </div>
                    )}
                  </div>
                </article>
              </li>
            );
          })}
        </ol>

        <div className="mt-10 flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-end">
          {/* Embed props matter here: in the mobile WebView the back-out must
            post `bozp:close` (verdict = passes so far), NOT navigate to the
            `fixitapp://` return link - the host intercepts that link as
            "everything done". */}
          <ReturnButton returnUrl={returnUrl} embed={embed} parentOrigin={parentOrigin} />
          <button
            type="button"
            data-testid="hub-all-done"
            disabled={!allDone}
            onClick={() => {
              if (allDone && returnUrl) window.location.assign(returnUrl);
            }}
            className="hb-btn hb-btn--primary"
          >
            {t('All done')}
          </button>
        </div>
      </main>
    </div>
  );
}

function HubPage() {
  const navigate = useNavigate();
  const { trainings } = getSession();
  const ids = trainings.map((tr) => tr.hazardProfileId);

  // Fetch each required training so the hub shows its real title (not the raw UUID) and
  // chapter count. Shares the ['training', id] cache → warms the LMS route on open.
  const results = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['training', id],
      queryFn: () => getTraining(id),
      staleTime: Number.POSITIVE_INFINITY,
    })),
  });

  const titleById: Record<string, string> = {};
  results.forEach((r, i) => {
    if (r.data) titleById[ids[i]] = r.data.title;
  });

  // Per-training local progress (completed chapters / total) for the card progress bars.
  const [progressById, setProgressById] = useState<Record<string, Progress>>({});
  const loadedKey = results.map((r) => (r.data ? r.data.id : '')).join(',');
  useEffect(() => {
    let alive = true;
    (async () => {
      const userId = decodeJwtSub(getBozpToken() ?? '') ?? 'anon';
      const map: Record<string, Progress> = {};
      for (let i = 0; i < ids.length; i++) {
        const data = results[i]?.data;
        if (!data) continue;
        const p = await loadProgress(userId, ids[i]);
        map[ids[i]] = {
          done: p?.completedChapterIds.length ?? 0,
          total: data.content.chapters.length,
        };
      }
      if (alive) setProgressById(map);
    })();
    return () => {
      alive = false;
    };
    // results is recreated each render; loadedKey captures the meaningful "which loaded" signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedKey]);

  // fixit/4329: the landing breadcrumb, once its training reads settle, plus
  // every read that failed. A device that stalls here is then visible in the
  // API log (the 2026-09-28 stall left one training GET and nothing else).
  const settledKey = results.map((r) => (r.isPending ? '' : r.status)).join(',');
  useEffect(() => {
    if (results.some((r) => r.isPending)) return;
    results.forEach((r, i) => {
      if (r.isError) reportClientError(r.error, { hazardProfileId: ids[i], stage: 'hub_training' });
    });
    reportLandingLoaded({
      trainings: ids.length,
      loaded: results.filter((r) => r.isSuccess).length,
    });
  }, [settledKey]);

  return (
    <HubView
      onOpenTraining={(hazardId) => navigate({ to: '/training/$hazardId', params: { hazardId } })}
      titleById={titleById}
      progressById={progressById}
    />
  );
}
